import React, { useState } from 'react'
import {
  Card,
  CardContent,
  Box,
  Stack,
  Typography,
  Collapse,
  IconButton,
  Divider,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material'
import { HealthStatusBadge } from './HealthStatusBadge'
import { HealthCheckRow } from './HealthCheckRow'
import type { HealthCheck, HealthStatus } from '../../../types/healthCheck'
import { BAND_HEX } from '../../../utils/exceptionSeverity'

const SEVERITY_LABELS: Array<{
  key: 'critical' | 'poor' | 'watch' | 'good'
  label: string
  color: string
}> = [
  { key: 'critical', label: 'critical', color: BAND_HEX.Critical },
  { key: 'poor', label: 'poor', color: BAND_HEX.Poor },
  { key: 'watch', label: 'watch', color: BAND_HEX.Watch },
  { key: 'good', label: 'good', color: BAND_HEX.Good },
]

interface SeveritySummaryProps {
  counts: { critical: number; poor: number; watch: number; good: number }
}

const SeveritySummary: React.FC<SeveritySummaryProps> = ({ counts }) => {
  const active = SEVERITY_LABELS.filter((s) => counts[s.key] > 0)
  if (active.length === 0) return null
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 0.25 }}>
      {active.map((s, i) => (
        <Stack key={s.key} direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: s.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {counts[s.key]} {s.label}
          </Typography>
          {i < active.length - 1 && (
            <Typography variant="caption" color="text.disabled" sx={{ pl: 0.5 }}>
              ·
            </Typography>
          )}
        </Stack>
      ))}
    </Stack>
  )
}

interface HealthCheckSectionProps {
  name: string
  verdict: string
  checks: HealthCheck[]
  fpCode: string
  defaultExpanded?: boolean
}

export const HealthCheckSection: React.FC<HealthCheckSectionProps> = ({
  name,
  verdict,
  checks,
  fpCode,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [showPending, setShowPending] = useState(false)

  const liveChecks = checks.filter((c) => c.status !== 'pending')
  const pendingChecks = checks.filter((c) => c.status === 'pending')

  const counts = {
    critical: liveChecks.filter((c) => c.status === 'critical').length,
    poor: liveChecks.filter((c) => c.status === 'poor').length,
    watch: liveChecks.filter((c) => c.status === 'watch').length,
    good: liveChecks.filter((c) => c.status === 'good').length,
  }

  return (
    <Card variant="outlined">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          py: 1.75,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {name}
          </Typography>
          <SeveritySummary counts={counts} />
        </Box>
        <HealthStatusBadge status={verdict as HealthStatus} />
        <IconButton size="small" sx={{ ml: 1 }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <CardContent sx={{ px: 2.5, py: 1.25, '&:last-child': { pb: 1.5 } }}>
          {liveChecks.map((check) => (
            <HealthCheckRow key={check.key} check={check} fpCode={fpCode} />
          ))}
          {pendingChecks.length > 0 && (
            <>
              {liveChecks.length > 0 && <Divider sx={{ my: 1 }} />}
              <Box
                onClick={(e) => {
                  e.stopPropagation()
                  setShowPending(!showPending)
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  py: 0.75,
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {pendingChecks.length} coming soon
                </Typography>
                <IconButton size="small" sx={{ ml: 1 }}>
                  {showPending ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>
              <Collapse in={showPending}>
                {pendingChecks.map((check) => (
                  <HealthCheckRow key={check.key} check={check} fpCode={fpCode} />
                ))}
              </Collapse>
            </>
          )}
        </CardContent>
      </Collapse>
    </Card>
  )
}

export default HealthCheckSection
